/**
 * Case List View Queries
 *
 * React Query hooks for displaying case data in list views.
 * Uses mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedCaseListViews, persistedCaseRecords } from '../mockData/cases';
import { getRecordsForObjectPage, getRecordsForObject } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import { getCreditBlockClientIds } from './creditBlockQueries';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';

// Mock list views for Cases
const mockCaseListViews: ListView[] = [
  {
    id: 'lv_cases_all',
    name: 'All Cases',
    apiName: 'all_cases',
    objectApiName: 'Case',
    description: 'All case records',
    isDefault: true,
    columns: [
      // Use Gobodo field names with __c suffix for API compatibility
      { fieldApiName: 'CaseNumber__c', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Subject__c', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'Description__c', fieldLabel: 'Description', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_cases_open',
    name: 'Open Cases',
    apiName: 'open_cases',
    objectApiName: 'Case',
    description: 'Active cases not yet closed',
    isDefault: false,
    columns: [
      { fieldApiName: 'CaseNumber__c', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Subject__c', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'RelatedRecordName__c', fieldLabel: 'Related To', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_cases_escalated',
    name: 'Escalated Cases',
    apiName: 'escalated_cases',
    objectApiName: 'Case',
    description: 'Cases that have been escalated',
    isDefault: false,
    columns: [
      { fieldApiName: 'CaseNumber__c', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Subject__c', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'RelatedRecordName__c', fieldLabel: 'Related To', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'IsEscalated__c', operator: 'equals', value: true }
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_cases_critical',
    name: 'Critical Priority',
    apiName: 'critical_priority_cases',
    objectApiName: 'Case',
    description: 'Critical and high priority cases',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'contactName', fieldLabel: 'Contact', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  },
  {
    id: 'lv_cases_by_product',
    name: 'By Product',
    apiName: 'cases_by_product',
    objectApiName: 'Case',
    description: 'Cases grouped by product/service',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'contactName', fieldLabel: 'Contact', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    sortBy: {
      fieldApiName: 'productName',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-05-01T10:00:00Z'
  }
];

// Mock case records
const mockCaseRecords: ListViewRecord[] = [
  {
    id: 'case_001',
    caseNumber: 'C-00001',
    subject: 'Unable to access account after password reset',
    description: 'Customer reports being locked out after attempting password reset',
    status: 'In Progress',
    priority: 'High',
    type: 'Account Issue',
    origin: 'Email',
    contactName: 'Sarah Martinez',
    accountName: 'Tech Innovations Inc',
    productName: 'Credit Repair',
    owner: 'Emily Davis',
    createdDate: '2025-01-15T09:30:00Z',
    lastModifiedDate: '2025-01-16T14:20:00Z',
    responseTime: 2,
    escalated: false
  },
  {
    id: 'case_002',
    caseNumber: 'C-00002',
    subject: 'Software crash during data export',
    description: 'Application crashes when exporting large datasets',
    status: 'Escalated',
    priority: 'Critical',
    type: 'Bug Report',
    origin: 'Phone',
    contactName: 'Michael Chen',
    accountName: 'Global Manufacturing Corp',
    productName: 'ID Theft Recovery',
    owner: 'David Wilson',
    createdDate: '2025-01-14T11:00:00Z',
    lastModifiedDate: '2025-01-16T16:45:00Z',
    responseTime: 1,
    escalated: true
  },
  {
    id: 'case_003',
    caseNumber: 'C-00003',
    subject: 'Request for custom reporting feature',
    description: 'Client needs quarterly performance dashboards',
    status: 'New',
    priority: 'Medium',
    type: 'Feature Request',
    origin: 'Web',
    contactName: 'Emily Rodriguez',
    accountName: 'Retail Solutions LLC',
    productName: 'Credit Development',
    owner: 'John Smith',
    createdDate: '2025-01-16T15:00:00Z',
    lastModifiedDate: '2025-01-16T15:00:00Z',
    escalated: false
  },
  {
    id: 'case_004',
    caseNumber: 'C-00004',
    subject: 'How to configure multi-factor authentication',
    description: 'Customer needs guidance on MFA setup',
    status: 'Waiting on Customer',
    priority: 'Low',
    type: 'Question',
    origin: 'Chat',
    contactName: 'David Park',
    accountName: 'Financial Services Group',
    productName: 'Credit Monitoring',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-13T10:30:00Z',
    lastModifiedDate: '2025-01-15T09:15:00Z',
    responseTime: 3,
    escalated: false
  },
  {
    id: 'case_005',
    caseNumber: 'C-00005',
    subject: 'Integration with third-party CRM failing',
    description: 'API connection errors when syncing with Salesforce',
    status: 'Waiting on Engineering',
    priority: 'High',
    type: 'Problem',
    origin: 'Email',
    contactName: 'Jennifer Taylor',
    accountName: 'Healthcare Innovations',
    productName: 'Credit Repair',
    owner: 'Michael Chen',
    createdDate: '2025-01-12T08:00:00Z',
    lastModifiedDate: '2025-01-16T11:30:00Z',
    responseTime: 4,
    escalated: false
  },
  {
    id: 'case_006',
    caseNumber: 'C-00006',
    subject: 'Billing discrepancy on January invoice',
    description: 'Charges do not match contracted rates',
    status: 'Resolved',
    priority: 'Medium',
    type: 'Account Issue',
    origin: 'Phone',
    contactName: 'Robert Anderson',
    accountName: 'Construction Pro LLC',
    productName: 'ID Theft Recovery',
    owner: 'Emily Davis',
    createdDate: '2025-01-10T14:00:00Z',
    closedDate: '2025-01-12T16:30:00Z',
    lastModifiedDate: '2025-01-12T16:30:00Z',
    responseTime: 2,
    resolutionTime: 50,
    escalated: false
  },
  {
    id: 'case_007',
    caseNumber: 'C-00007',
    subject: 'Performance degradation during peak hours',
    description: 'System slows significantly between 2-4 PM daily',
    status: 'Escalated',
    priority: 'Critical',
    type: 'Problem',
    origin: 'Email',
    contactName: 'Lisa Wong',
    accountName: 'Education Tech Solutions',
    productName: 'Credit Development',
    owner: 'David Wilson',
    createdDate: '2025-01-11T13:30:00Z',
    lastModifiedDate: '2025-01-16T10:00:00Z',
    responseTime: 1,
    escalated: true
  },
  {
    id: 'case_008',
    caseNumber: 'C-00008',
    subject: 'Mobile app not syncing with web version',
    description: 'Changes made on mobile do not appear on desktop',
    status: 'In Progress',
    priority: 'Medium',
    type: 'Bug Report',
    origin: 'Web',
    contactName: 'James Miller',
    accountName: 'Logistics Central',
    productName: 'Credit Monitoring',
    owner: 'John Smith',
    createdDate: '2025-01-14T09:00:00Z',
    lastModifiedDate: '2025-01-16T13:45:00Z',
    responseTime: 5,
    escalated: false
  },
  {
    id: 'case_009',
    caseNumber: 'C-00009',
    subject: 'Request for API documentation updates',
    description: 'Current API docs missing webhook examples',
    status: 'New',
    priority: 'Low',
    type: 'Feature Request',
    origin: 'Social Media',
    contactName: 'Amanda Foster',
    accountName: 'Marketing Pros',
    productName: 'Credit Repair',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-16T11:00:00Z',
    lastModifiedDate: '2025-01-16T11:00:00Z',
    escalated: false
  },
  {
    id: 'case_010',
    caseNumber: 'C-00010',
    subject: 'User permissions not saving correctly',
    description: 'Role changes revert after logout',
    status: 'Escalated',
    priority: 'High',
    type: 'Bug Report',
    origin: 'Phone',
    contactName: 'Christopher Lee',
    accountName: 'Software Dynamics',
    productName: 'ID Theft Recovery',
    owner: 'Michael Chen',
    createdDate: '2025-01-15T16:00:00Z',
    lastModifiedDate: '2025-01-16T15:30:00Z',
    responseTime: 2,
    escalated: true
  },
  {
    id: 'case_011',
    caseNumber: 'C-00011',
    subject: 'Training request for new team members',
    description: 'Need onboarding session for 5 new users',
    status: 'Closed',
    priority: 'Low',
    type: 'Question',
    origin: 'Email',
    contactName: 'Sarah Martinez',
    accountName: 'Tech Innovations Inc',
    productName: 'Credit Development',
    owner: 'Emily Davis',
    createdDate: '2025-01-08T10:00:00Z',
    closedDate: '2025-01-11T14:00:00Z',
    lastModifiedDate: '2025-01-11T14:00:00Z',
    responseTime: 6,
    resolutionTime: 76,
    escalated: false
  },
  {
    id: 'case_012',
    caseNumber: 'C-00012',
    subject: 'Email notifications not being received',
    description: 'Alerts and updates not arriving in inbox',
    status: 'Resolved',
    priority: 'Medium',
    type: 'Problem',
    origin: 'Chat',
    contactName: 'David Park',
    accountName: 'Financial Services Group',
    productName: 'Credit Monitoring',
    owner: 'John Smith',
    createdDate: '2025-01-13T14:30:00Z',
    closedDate: '2025-01-15T10:20:00Z',
    lastModifiedDate: '2025-01-15T10:20:00Z',
    responseTime: 3,
    resolutionTime: 44,
    escalated: false
  },
  {
    id: 'case_013',
    caseNumber: 'C-00013',
    subject: 'Data migration assistance needed',
    description: 'Help required to migrate from legacy system',
    status: 'In Progress',
    priority: 'High',
    type: 'Problem',
    origin: 'Phone',
    contactName: 'Jennifer Taylor',
    accountName: 'Healthcare Innovations',
    productName: 'Credit Repair',
    owner: 'David Wilson',
    createdDate: '2025-01-09T09:00:00Z',
    lastModifiedDate: '2025-01-16T12:00:00Z',
    responseTime: 2,
    escalated: false
  },
  {
    id: 'case_014',
    caseNumber: 'C-00014',
    subject: 'Dashboard widgets not loading',
    description: 'Charts and graphs show loading spinner indefinitely',
    status: 'New',
    priority: 'Medium',
    type: 'Bug Report',
    origin: 'Web',
    contactName: 'Lisa Wong',
    accountName: 'Education Tech Solutions',
    productName: 'ID Theft Recovery',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-16T14:00:00Z',
    lastModifiedDate: '2025-01-16T14:00:00Z',
    escalated: false
  },
  {
    id: 'case_015',
    caseNumber: 'C-00015',
    subject: 'Bulk import feature enhancement',
    description: 'Request to support CSV files larger than 100MB',
    productName: 'Credit Development',
    status: 'Waiting on Engineering',
    priority: 'Low',
    type: 'Feature Request',
    origin: 'Email',
    contactName: 'James Miller',
    accountName: 'Logistics Central',
    owner: 'Michael Chen',
    createdDate: '2025-01-12T11:30:00Z',
    lastModifiedDate: '2025-01-14T16:00:00Z',
    responseTime: 8,
    escalated: false
  }
];

/**
 * Get available list views for cases
 */
const CREDIT_BLOCK_VIEW: ListView = {
  id: 'lv_cases_credit_block',
  name: 'Credit Block Cases',
  apiName: 'credit_block_cases',
  objectApiName: 'Case',
  description: 'Cases linked to Credit Block clients',
  isDefault: false,
  columns: [
    { fieldApiName: 'id', fieldLabel: 'Case ID', fieldType: 'text', isVisible: true, order: 0 },
    { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 1 },
    { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 2 },
    { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
    { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
    { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 5 },
    { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', isVisible: true, order: 6 },
    { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 7 },
  ],
  createdBy: 'System',
  createdDate: '2024-06-01T00:00:00Z',
};

export function useCaseListViews() {
  return useQuery({
    queryKey: ['caseListViews'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      const views = persistedCaseListViews.get();
      if (!views.some((v) => v.id === 'lv_cases_credit_block')) {
        const merged = [...views, CREDIT_BLOCK_VIEW];
        persistedCaseListViews.set(merged);
        return merged;
      }
      return views;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export const DEFAULT_CASE_PAGE_SIZE = 50;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useCaseListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_CASE_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Case'));

  return useQuery<ListViewData>({
    queryKey: ['caseListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedCaseListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const isCreditBlockView = listView.id === 'lv_cases_credit_block';
        let pageRecords: ListViewRecord[];
        let totalRecords: number;

        if (isCreditBlockView && shouldUseGobodo()) {
          const [clientIds, allCases] = await Promise.all([
            getCreditBlockClientIds(),
            getRecordsForObject('Case', { size: 50 }),
          ]);
          const norm = (s: string) => (s ?? '').trim();
          const strip = (s: string) => s.replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
          const idSet = new Set(clientIds.map(strip).filter(Boolean));
          const matches = (r: Record<string, unknown>) => {
            const raw = (r as { accountId?: string }).accountId ?? (r as Record<string, unknown>).AccountId__c ?? '';
            const aid = norm(typeof raw === 'string' ? raw : String(raw ?? ''));
            return aid && (idSet.has(aid) || idSet.has(strip(aid)));
          };
          const filtered = (allCases as Record<string, unknown>[]).filter(matches);
          totalRecords = filtered.length;
          const start = page * pageSize;
          pageRecords = filtered.slice(start, start + pageSize).map(r => {
            const normRec = normalizeRecordForListView('Case', r);
            return { ...normRec, name: normRec.name ?? normRec.label ?? normRec.subject ?? (normRec as any).Subject__c ?? r.id };
          }) as ListViewRecord[];
        } else {
          const result = await getRecordsForObjectPage('Case', { page, size: pageSize });
          pageRecords = result.records.map(r => {
            const norm = normalizeRecordForListView('Case', r);
            return { ...norm, name: norm.name ?? norm.label ?? norm.subject ?? (norm as any).Subject__c ?? r.id };
          });
          totalRecords = result.totalRecords;
        }

        let records = pageRecords;
        let effectiveTotal = totalRecords;
        if (effectiveTotal === 0 && pageRecords.length === pageSize && !isCreditBlockView) {
          effectiveTotal = page * pageSize + pageRecords.length + pageSize;
        }

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals': return fieldValue === filter.value;
                case 'notEquals': return fieldValue !== filter.value;
                case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default: return true;
              }
            });
          });
        }
        if (listView.id === 'lv_cases_open') {
          records = records.filter(record => {
            const status = record.Status__c || (record as any).status;
            return status !== 'Resolved' && status !== 'Closed';
          });
        }
        if (listView.id === 'lv_cases_critical') {
          records = records.filter(record => {
            const priority = record.Priority__c || (record as any).priority;
            return priority === 'Critical' || priority === 'High';
          });
        }
        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }
        return { listView, records, totalRecords: effectiveTotal };
      }

      const allRecords = persistedCaseRecords.get();
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals': return fieldValue === filter.value;
              case 'notEquals': return fieldValue !== filter.value;
              case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default: return true;
            }
          });
        });
      }
      if (listView.id === 'lv_cases_open') {
        filtered = filtered.filter(record => {
          const status = record.Status__c || (record as any).status;
          return status !== 'Resolved' && status !== 'Closed';
        });
      }
      if (listView.id === 'lv_cases_critical') {
        filtered = filtered.filter(record => {
          const priority = record.Priority__c || (record as any).priority;
          return priority === 'Critical' || priority === 'High';
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get default list view for cases
 */
export function useDefaultCaseListView() {
  return useQuery({
    queryKey: ['defaultCaseListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));

      const allViews = persistedCaseListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update a case list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateCaseListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update in localStorage via mockDataManager
      const updated = persistedCaseListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['caseListViews'] });
      queryClient.invalidateQueries({ queryKey: ['caseListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultCaseListView'] });
    },
  });
}
